import { useCallback, useRef, useSyncExternalStore } from 'react';
import { create } from 'zustand';
import { apiUrl } from '../api/api-url';
import { appClient, directClient, READ_TIMEOUT_MS, WRITE_TIMEOUT_MS, type HttpClient, type RequestOptions } from '../api/http';
import { HttpFailure } from '../api/http-failure';
import { toApiError, type ApiError } from '../api/api-error';
import type { ActionSummary } from '../api/types';
import { createRenewingClient } from '../http/renewing-client';
import { account, isSignedIn, onPractitionerChange, onSignedIn } from '../session/session.store';
import { advanceResync } from '../reach/resync.store';
import { evidenceCount, noteOutcome, subscribeToBeat } from '../reach/reach.store';
import { createLockRunner, type LockRunner } from '../concurrency/browser-lock';
import { clock } from '../time/clock';
import {
  CAPTURE_PATH,
  CHANGE_METHOD,
  TAGS_METHOD,
  changePath,
  deleteDeviceDatabase,
  isUnreadableRow,
  openDeviceDatabase,
  randomKey,
  tagsPath,
  toOutboxRow,
  type DeviceDatabase,
  type NewOutboxRecord,
  type OutboxRecord,
  type OutboxRow,
} from './device-database';
import { CURRENT_WIRE_VERSION, IDEMPOTENCY_KEY_HEADER, MADE_AT_HEADER, WIRE_VERSION_HEADER } from './replay-headers';

/**
 * 036 (issue 207) — `submitCapture(body)` and the send loop (research R-001, R-007, R-020;
 * data-model.md "Device").
 *
 * **REDESIGNED, not patched, after three rounds of isolation findings against the previous
 * module-level shape** (one database handle, one channel, one queue of passes, one map of waiters
 * — all belonging to "whoever is signed in", with nothing actually verifying that at the moment it
 * mattered). The security review's own finding, confirmed by reading the path end to end: a pass
 * bound to the practitioner id captured when it was SCHEDULED, checking only "is anyone signed in"
 * from then on, could resume after that practitioner's session ended and a DIFFERENT one signed in
 * — reopening the first practitioner's database and sending their rows under the second
 * practitioner's token, then filling the second practitioner's `/unsent` with the first's rows.
 *
 * **The fix is structural.** Everything the previous design spread across module-level variables
 * now belongs to ONE object per practitioner (`createOutbox`, below) — its own database handle, its
 * own channel, its own waiters, its own send loop, its own "who is currently in flight." The only
 * module-level state left is `current`, the ONE holder a small subscription (`watchSession`, below)
 * keeps pointed at the signed-in practitioner's own object and nothing else's. An outbox object
 * that is no longer current is `closed`: every one of its methods checks that flag after every
 * `await` and stops rather than act on stale authority, and its send loop re-checks "is the
 * signed-in practitioner still MINE" immediately before every request — not once per pass.
 *
 * **There is one path.** Every replayable write reaches the server through `submitCapture()` —
 * "How `submit` behaves" in the issue is the contract this file implements, and `only-door.spec.ts`
 * is what keeps a feature store from reaching around it. Nothing outside `core/outbox/` sees the
 * path or the request shape any more: a store that could once build
 * `{kind:'capture', method:'POST', path: CAPTURE_PATH, body}` for itself is exactly the shape the
 * guard could not see through `appClient().post(apiUrl(CAPTURE_PATH), body)` — the most natural
 * bypass, and one this branch itself had made possible by exporting the path at all.
 *
 * This pull request writes and sends `kind: 'capture'` items only. `device-database.ts`'s schema
 * already carries `kind: 'change'` and the `actionId`/`shows` fields a change needs; nothing here
 * constructs one yet (that is T089, Phase 3) — and `isKnownRecord` there now refuses to treat
 * anything but a capture to this exact route as sendable at all.
 */

export type SubmitOutcome<T = unknown> =
  | { readonly kind: 'answered'; readonly result: T }
  | { readonly kind: 'waiting' }
  /**
   * Carries the row's OWN `seq`, and the `practitionerId` it was refused FOR: the outbox always
   * keeps a refused row (never deletes it on the strength of a waiter existing), so a caller that
   * CAN still show the refusal where the line was typed is the one that decides to
   * remove it — by calling `dismissRefused(seq, practitionerId)` once it has shown the reason. A
   * caller that cannot (the screen has moved on) simply does not call it; the row stays, and the
   * unsent count is what tells the practitioner. `practitionerId` is what stops a dismiss issued by
   * a screen still holding a stale closure from a PREVIOUS practitioner's session from reaching the
   * CURRENT one's outbox at all — `dismissRefused` refuses unless it names the outbox that is
   * current right now.
   */
  | { readonly kind: 'refused'; readonly problem: ApiError; readonly seq: number; readonly practitionerId: number | string };

export type ReplaceOutcome =
  | { readonly kind: 'ok' }
  /** `'gone'` — the row no longer exists (already sent, already discarded, or never existed).
   *  `'sending'` — the row is in flight right now; edited a moment later, this would succeed.
   *  `'device'` — the device itself would not write the replacement (FR-013's own failure). */
  | { readonly kind: 'refused'; readonly reason: 'gone' | 'sending' | 'device' };

// --- seams (navigator.locks and BroadcastChannel do not exist in jsdom; research "Sending") ----

const outboxLock = createLockRunner('outbox');

/** Test seam. Production code never calls it. */
export function setLockRunner(next: LockRunner | null): void {
  outboxLock.setLockRunner(next);
}

export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  close(): void;
}

type ChannelFactory = (name: string) => ChannelLike | null;

const defaultChannelFactory: ChannelFactory = (name) => {
  const Ctor = (globalThis as { BroadcastChannel?: new (name: string) => ChannelLike }).BroadcastChannel;
  return Ctor ? new Ctor(name) : null;
};

let channelFactory: ChannelFactory = defaultChannelFactory;

/** Test seam. Production code never calls it. */
export function setChannelFactory(next: ChannelFactory | null): void {
  channelFactory = next ?? defaultChannelFactory;
}

/**
 * What one window's outbox tells every other window sharing its
 * practitioner, and why each is its OWN message rather than one "something changed" notice:
 *   - `changed` — a row's STATE differs (a refusal, a discard, an edit); refresh, nothing more.
 *   - `sent` — something was ANSWERED. The sending window already knows to advance `resync` for
 *     itself; a window that did NOT send it must be told to as well, or its screens never re-read
 *     what just landed on the server (FR-036) — `changed` alone would refresh `/unsent` there
 *     without ever nudging the container or review page showing the row that arrived.
 *   - `deleted` — the whole database is gone (a confirmed sign-out discarding what was left).
 *     Every other window's handle to it is now dead whether or not its own `blocking` handler has
 *     fired yet; this is what makes the mirror go empty AT ONCE rather than the next time something
 *     else happens to trigger a refresh that then fails.
 */
type ChannelMessage = { readonly type: 'changed' } | { readonly type: 'sent' } | { readonly type: 'deleted' };

// --- the mirror: what every component reads, always the CURRENT outbox's rows and nothing else --

interface MirrorState {
  readonly practitionerId: number | string | null;
  readonly rows: readonly OutboxRow[];
  /** FR-013: the device refused to keep the last thing this module tried to store. Cleared the
   *  next time a write to the device succeeds. */
  readonly cannotStore: boolean;
  /** Which rows this window's outbox is sending RIGHT NOW: the request
   *  is on the wire, not merely `waiting` for a turn. `/unsent` uses this to withhold Edit and
   *  Discard from a row a reply could arrive for at any moment. */
  readonly sending: ReadonlySet<number>;
}

const EMPTY_MIRROR: MirrorState = { practitionerId: null, rows: [], cannotStore: false, sending: new Set() };

const mirror = create<MirrorState>()(() => ({ ...EMPTY_MIRROR }));

export const unsentRows = {
  use: (): readonly OutboxRow[] => mirror((s) => s.rows),
  get: (): readonly OutboxRow[] => mirror.getState().rows,
};

/** FR-012: every item in the outbox counts, whatever its state — refused and in-conflict items are
 *  exactly as "not sent" as a waiting one, and `/unsent` is where all of them are seen. */
export const unsentCount = {
  use: (): number => mirror((s) => s.rows.length),
  get: (): number => mirror.getState().rows.length,
};

export const deviceCannotStore = {
  use: (): boolean => mirror((s) => s.cannotStore),
  get: (): boolean => mirror.getState().cannotStore,
};

/** Which rows this window's outbox is sending RIGHT NOW. `/unsent` reads this to decide whether a
 *  row offers Edit/Discard. */
export const sendingSeqs = {
  use: (): ReadonlySet<number> => mirror((s) => s.sending),
  get: (): ReadonlySet<number> => mirror.getState().sending,
};

/**
 * 036 T089 (R-019) — "what is waiting for me": every unsent `change` row for one action, in the
 * order they were made. A REAL subscription (F-006's own repair) — this reads the same `mirror`
 * store every other export on this page already does, through Zustand's own selector hook, so a
 * mounted caller re-renders exactly when `mirror`'s state changes: an item queued, sent, withdrawn
 * or refused. Rows elsewhere read `pending` once at render and never subscribe (F-006); this is the
 * pattern that repairs it, not a second instance of the bug.
 *
 * An action can carry more than one queued change at once (a star and a tick made separately,
 * offline) — every one of them is returned, seq-ordered, so a caller can fold their `shows` together
 * itself; this file has no opinion about how they combine, only about which rows exist.
 */
/** Content equality for the small set of fields a change row carries — never identity, because
 *  `refreshMirrorFor()` rebuilds EVERY row in `mirror.rows` fresh from the device on any event, this
 *  action's own rows included when the event was some OTHER action's; two rows with identical
 *  content but different object identity must still read as "the same" or `useUnsentFor` would
 *  re-render every subscriber on every unrelated write, exactly the "not otherwise" criterion 4
 *  forbids. Rows are small and JSON-safe (no functions, no non-plain values), so this is correct
 *  and simple rather than a hand-rolled field-by-field walk that could quietly miss a field later. */
function sameChangeRows(a: readonly OutboxRow[], b: readonly OutboxRow[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((row, i) => JSON.stringify(row) === JSON.stringify(b[i]));
}

/**
 * 036 T089 (R-019) — "what is waiting for me": every unsent `change` row for one action, in the
 * order they were made. A REAL subscription (F-006's own repair): a mounted caller re-renders
 * exactly when a row for ITS OWN actionId is queued, sent, withdrawn or refused — and not otherwise.
 *
 * **Not built on the `mirror` zustand hook directly.** Zustand 5's default `create()` hook (this
 * file's `mirror`) takes a selector ONLY — no equality function — and calls `React.useSyncExternalStore`
 * with it as-is. A selector that FILTERS (`s.rows.filter(...)`) returns a new array on every
 * invocation, including the internal ones `useSyncExternalStore` makes to check whether anything
 * changed, which read as "changed" for ever and loop ("Maximum update depth exceeded" — measured
 * directly, not assumed, while writing `use-unsent-for.spec.tsx`). This calls
 * `React.useSyncExternalStore` itself, with a `getSnapshot` that caches its own last filtered
 * result and hands back that SAME reference whenever the newly filtered rows are unchanged in
 * content (`sameChangeRows`) — which is also what stops a re-render for an unrelated action's
 * write, since `mirror.rows`'s own reference changes on every event this file makes, related or not.
 *
 * An action can carry more than one queued change at once (a star and a tick made separately,
 * offline) — every one of them is returned, seq-ordered, so a caller can fold their `shows` together
 * itself; this file has no opinion about how they combine, only about which rows exist.
 */
export function useUnsentFor(actionId: number): readonly OutboxRow[] {
  const cache = useRef<{ readonly rows: readonly OutboxRow[]; readonly actionId: number; readonly result: readonly OutboxRow[] } | null>(null);

  const getSnapshot = useCallback((): readonly OutboxRow[] => {
    const rows = mirror.getState().rows;
    if (cache.current && cache.current.rows === rows && cache.current.actionId === actionId) {
      return cache.current.result;
    }
    const filtered = rows.filter((r) => !isUnreadableRow(r) && r.kind === 'change' && r.actionId === actionId);
    const result = cache.current && cache.current.actionId === actionId && sameChangeRows(cache.current.result, filtered)
      ? cache.current.result
      : filtered;
    cache.current = { rows, actionId, result };
    return result;
  }, [actionId]);

  return useSyncExternalStore(mirror.subscribe, getSnapshot, getSnapshot);
}

// --- the outbox object: everything that used to be module-level, now owned by ONE instance ------

interface Outbox {
  readonly practitionerId: number | string;
  closed: boolean;
  db: DeviceDatabase | null;
  channel: ChannelLike | null;
  readonly waiters: Map<number, (outcome: SubmitOutcome) => void>;
  readonly sendingSeqs: Set<number>;
  sending: boolean;
  kicked: boolean;
  /** What `flushOutbox()` awaits: the currently in-progress (and any coalesced) run, or `null`
   *  when nothing is running. Set by `kick()`, cleared by `runPasses()` when it finishes. */
  runningPromise: Promise<void> | null;
}

function createOutbox(practitionerId: number | string): Outbox {
  return {
    practitionerId,
    closed: false,
    db: null,
    channel: null,
    waiters: new Map(),
    sendingSeqs: new Set(),
    sending: false,
    kicked: false,
    runningPromise: null,
  };
}

/**
 * The ONE holder. Nothing about an outbox is reachable except through
 * this, and through it only for the practitioner it actually belongs to.
 */
let current: Outbox | null = null;

/** "after every await, check `closed` and stop." Opens lazily, and NEVER
 *  caches a handle once it reads closed: a connection the BROWSER closed (another
 *  tab's delete, another open of the same database) is forgotten here, not reused until it throws. */
async function dbFor(outbox: Outbox): Promise<DeviceDatabase> {
  if (outbox.db && !outbox.db.closed) return outbox.db;
  outbox.db = null;
  const db = await openDeviceDatabase(outbox.practitionerId);
  if (outbox.closed) {
    db.close();
    return db;
  }
  outbox.db = db;
  return db;
}

async function refreshMirrorFor(outbox: Outbox): Promise<void> {
  if (outbox.closed) return;
  const db = await dbFor(outbox);
  if (outbox.closed) return;
  const rows = await db.allRows();
  if (outbox.closed) return;
  mirror.setState({
    practitionerId: outbox.practitionerId,
    rows,
    cannotStore: mirror.getState().cannotStore,
    sending: new Set(outbox.sendingSeqs),
  });
}

function closeChannel(outbox: Outbox): void {
  outbox.channel?.close();
  outbox.channel = null;
}

function ensureChannel(outbox: Outbox): ChannelLike | null {
  if (outbox.channel) return outbox.channel;
  const channel = channelFactory(`completeme.outbox.send.${outbox.practitionerId}`);
  outbox.channel = channel;
  channel?.addEventListener('message', (event) => {
    if (outbox.closed || outbox !== current) return;
    const message = event.data as ChannelMessage;
    if (message.type === 'deleted') {
      // The database this outbox's handle names is gone. Forget the handle (never reuse a closed
      // one) and go empty at once, rather than waiting for the next unrelated trigger to discover
      // the same thing the slow way (a thrown `InvalidStateError`).
      outbox.db?.close();
      outbox.db = null;
      mirror.setState({ ...EMPTY_MIRROR, practitionerId: outbox.practitionerId });
      return;
    }
    if (message.type === 'sent') {
      void refreshMirrorFor(outbox).catch((error) => {
        console.warn('outbox: mirror refresh after another window\'s send failed', error);
      });
      advanceResync(); // FR-036: this window did not send it, so it must re-read too.
      return;
    }
    void refreshMirrorFor(outbox).catch((error) => {
      console.warn('outbox: mirror refresh after another window\'s change failed', error);
    });
  });
  return channel;
}

function announce(outbox: Outbox, type: ChannelMessage['type']): void {
  if (outbox.closed) return;
  ensureChannel(outbox)?.postMessage({ type } satisfies ChannelMessage);
}

function publishSendingSeqs(outbox: Outbox): void {
  if (outbox !== current || outbox.closed) return;
  mirror.setState({ sending: new Set(outbox.sendingSeqs) });
}

/**
 * The per-row half of "no longer quadratic over a backlog". Removing (on an
 * answer) or patching (on a refusal) the ONE row a pass just finished with, IN THE MIRROR ALREADY
 * HELD, rather than a full `db.allRows()` re-read after every row — the read that made a 200-item
 * backlog cost 200 device reads in this window on top of the 200 broadcasts `announce` used to send.
 * Purely synchronous (no device access, so nothing here can throw the way a device read could), and
 * harmless if `seq` is not present in the mirror at all — a real re-read at the END of the whole
 * pass (`runOnePass`'s `finally`) is what reconciles the mirror against the device's ACTUAL state,
 * including anything this row-by-row shortcut could not see (another window's own write, mid-pass).
 */
function removeRowFromMirror(outbox: Outbox, seq: number): void {
  if (outbox !== current || outbox.closed) return;
  mirror.setState((s) => ({ rows: s.rows.filter((r) => r.seq !== seq) }));
}

function patchRowInMirror(outbox: Outbox, seq: number, patch: Partial<Pick<OutboxRecord, 'state' | 'problem' | 'theirs'>>): void {
  if (outbox !== current || outbox.closed) return;
  mirror.setState((s) => ({
    rows: s.rows.map((r) => (r.seq === seq && !isUnreadableRow(r) ? { ...r, ...patch } : r)),
  }));
}

/**
 * 036 (issue 240 follow-up, SC-020) — the write-side twin of `removeRowFromMirror`: appends the row
 * `submitCapture`/`submitChange` just wrote, IN THE MIRROR ALREADY HELD, instead of the full
 * `db.allRows()` scan `refreshMirrorFor` used to be awaited for before either function returned.
 *
 * **The first measurement of this gap was wrong, and the mistake is worth keeping on record.** A
 * first pass at SC-020 measured a queued change's own mark at a median of 83ms against
 * `origin/develop`'s 34ms using Playwright's `locator.click()` — which carries its own
 * actionability-check and CDP round-trip cost that a real tap does not. Profiling with
 * `performance.now()` marks through `submitChange` (a throwaway build, not committed) found the
 * function itself finishing in single-digit milliseconds; re-measured with a native `element.click()`
 * and in-page timestamps (this cycle's `e2e-pwa/timing-sc020.spec.ts`), the two branches came out at
 * 21ms and 18ms — comfortably inside the 50ms budget before this function existed at all. It is kept,
 * and written up, anyway: skipping a full cursor scan on a path that only ever appends one row it
 * already holds every field of is a correctness-neutral, strictly-cheaper change on its own terms,
 * proven not to be free (`refreshMirrorFor`'s own read is real work) even though it was not, in the
 * end, the answer to the number this was first built to fix.
 *
 * Purely synchronous (no device access, so nothing here can throw the way a device read could), and
 * safe to call whether or not this exact row ends up matching anything a later full refresh finds
 * (that refresh still runs, and still REPLACES `rows` outright, so it can never see this row twice).
 *
 * **The rule that must not move**: this is called only AFTER `db.addCapture` has already resolved —
 * never before, and never on the path where it throws. A row appears in the mirror if and only if it
 * is already durably on the device, exactly as `submitCapture`'s own existing ordering already
 * guarantees for the FULL refresh this shortcuts.
 */
function insertRowIntoMirror(outbox: Outbox, record: NewOutboxRecord, seq: number): void {
  if (outbox !== current || outbox.closed) return;
  const row = toOutboxRow(seq, record);
  mirror.setState((s) => ({ rows: [...s.rows, row] }));
}

/**
 * `close()`. Every waiter still open is settled as `waiting` (FR-054's
 * own point: the caller already treats that outcome correctly), and both the handle and the
 * channel are closed so nothing about this object can act again, however delayed.
 */
function closeOutbox(outbox: Outbox): void {
  outbox.closed = true;
  outbox.db?.close();
  outbox.db = null;
  closeChannel(outbox);
  for (const [seq, resolve] of [...outbox.waiters]) {
    outbox.waiters.delete(seq);
    resolve({ kind: 'waiting' });
  }
  outbox.sendingSeqs.clear();
}

/**
 * "immediately before each request, not once per pass": the signed-in
 * practitioner must still BE this outbox's own, checked fresh every time this is called.
 */
function stillMine(outbox: Outbox): boolean {
  return isSignedIn.get() && account.get()?.practitionerId === outbox.practitionerId;
}

// --- sending -------------------------------------------------------------------------------------

/**
 * Built fresh per attempt: `directClient()` is a test seam (`setDirectClient`), and caching the
 * wrapper across a spec's `beforeEach` would freeze it around a stale inner client.
 *
 * `mayRetry: () => stillMine(outbox)` pins the RETRY, not just the send, to this outbox's own
 * practitioner. Without it, a renewal that the renewing client runs on a 401 can succeed
 * perfectly validly — for whoever is CURRENTLY signed in, if that is no longer this outbox's own
 * practitioner (their send was merely the one that happened to be in flight when the 401 landed) —
 * and the client would then retry with the new token regardless, sending this row into the wrong
 * account. `stillMine` is already re-checked immediately before every request in `runOnePass`; this
 * is the same check, reached a different way — after the retry's OWN renewal, which the per-row
 * check in the loop cannot see because it runs before `dispatch` is ever called.
 */
function sendingClient(outbox: Outbox): HttpClient {
  return createRenewingClient(directClient(), { mayRetry: () => stillMine(outbox) });
}

/**
 * 036 T089 — this used to be `client.post(...)` unconditionally, correct while a capture was the
 * only kind this file ever sent. A `change`'s own method is `PATCH` (`CHANGE_METHOD`,
 * `device-database.ts`); dispatching every record as a POST regardless of its own `request.method`
 * would send a change to the right PATH under the wrong VERB, which this build's own fake HTTP
 * client already distinguishes (`http.match({ method, url })`) and a real server would refuse
 * outright. The record states which verb it was queued under (FR-057's own "what is sent" field);
 * this is the one place that reads it back.
 */
function dispatch(client: HttpClient, method: string, url: string, body: unknown, options: RequestOptions): Promise<unknown> {
  switch (method) {
    case 'PATCH': return client.patch(url, body, options);
    case 'PUT': return client.put(url, body, options);
    case 'POST': return client.post(url, body, options);
    default: return client.post(url, body, options); // every route this build queues today is POST, PATCH or PUT
  }
}

/**
 * Which statuses mean "try again", not "refused". `0` is no answer at all
 * (`http.ts`'s own status for a `fetch` that never completed); `401` and `408` are the server
 * declining to answer THIS request right now rather than a verdict on it; every `5xx` is the
 * server's own failure, not the practitioner's. Everything else is a considered refusal with a
 * reason worth showing.
 */
function isWaitingStatus(status: number): boolean {
  return status === 0 || status === 401 || status === 408 || (status >= 500 && status < 600);
}

/**
 * A send has a time limit. `http.ts` had no `AbortSignal` at all, so one
 * stalled request held the send lock (fix 1's own lock, `completeme.outbox.send.<practitionerId>`)
 * and the capture box behind it for as long as the browser allowed, in every window. Ten seconds,
 * not the shorter figure a read might use: a slow but genuinely answering server must not be read
 * as `waiting` before it had a fair chance to.
 *
 * 036 (issue 208): this used to be the one place `10_000` was written down. `http.ts` now defaults
 * every write to the SAME number when a caller states none at all (`WRITE_TIMEOUT_MS`), so this is
 * an alias rather than a second definition — kept as its own name because every call site below
 * still reads `SEND_TIMEOUT_MS` and a send's reason for the number (a slow-but-real answer) is not a
 * write's in general.
 */
const SEND_TIMEOUT_MS = WRITE_TIMEOUT_MS;

/**
 * A `2xx` is an answer only if it IS one. `attemptSend` used to treat any `2xx`
 * as answered and delete the row on the strength of it; a proxy, a captive portal or this
 * application's OWN SPA fallback answering `200` with an HTML page (or nothing at all) would then
 * delete the only copy of a write that never reached the API. Narrow to exactly
 * `{ action: { id: <number> } }`, not merely "parsed as an object" — this build's two routes
 * (`POST /actions/quick`'s `QuickCaptureResult` and `PATCH /actions/{id}`'s `ActionUpdateResult`,
 * 036 T089) both answer with `action` at the top, so one shape check covers a capture and a
 * change alike; neither this function nor its caller ever looks past `.action.id`.
 */
function isCaptureAnswer(value: unknown): value is { readonly action: { readonly id: number } } {
  if (!value || typeof value !== 'object') return false;
  const action = (value as Record<string, unknown>)['action'];
  if (!action || typeof action !== 'object') return false;
  return typeof (action as Record<string, unknown>)['id'] === 'number';
}

/**
 * 036 (issue 246) — a THIRD answer shape, found reading the tags route's own contract rather than
 * assumed: `PUT /api/v1/actions/{id}/tags` answers with the `ActionSummary` itself, at the top
 * level (`schema.d.ts`'s own response for that route; `shared/actions/ui/tag-editor.tsx`'s existing
 * direct call already reads it this way) — never wrapped in `{ action: ... }` the way a capture or a
 * field-edit change answers. A row queued by `submitTagsChange` (its own `baseNames` field is how a
 * stored row says so, `isKnownRecord`'s own distinction) is checked against `{ id: number }`
 * instead; every other row keeps the original `isCaptureAnswer` check. Without this, a genuinely
 * successful tags write would read as a malformed `2xx` forever, and the bad-row rule (issue 208)
 * would eventually mark a row the server had already applied as `refused`.
 */
function isWellFormedAnswer(record: OutboxRecord, value: unknown): boolean {
  if (record.baseNames !== undefined) {
    return !!value && typeof value === 'object' && typeof (value as Record<string, unknown>)['id'] === 'number';
  }
  return isCaptureAnswer(value);
}

// --- conflict resolution (issue 246, FR-033) -------------------------------------------------

/** `readConflictValue`'s own answer. `'learned'` — the action was read; `theirs` is the server's
 *  current value for whatever `base`/`baseNames` named. `'gone'` — the read itself 404'd, so the
 *  action is gone rather than merely conflicting; `'inconclusive'` — the read could not be tried
 *  (the session moved on) or did not complete (no answer, or any other failure); the row is left
 *  exactly `waiting` for the caller to retry, together with the write that found the conflict. */
type ConflictRead =
  | { readonly kind: 'learned'; readonly theirs: Readonly<Record<string, unknown>> }
  | { readonly kind: 'gone' }
  | { readonly kind: 'inconclusive' };

/**
 * "The client reads the action to learn the current value" (contracts/replayable-writes.md). One
 * `GET` to the SAME route a field-edit change PATCHes (`changePath` — a tags change's own route
 * answers `ActionSummary` too, so one read serves both). Uses the outbox's own `sendingClient`
 * (the renewing wrapper, pinned to `stillMine` exactly like the write that found the conflict) and
 * `stillMine` is checked fresh first — this is another request on the wire, held to the same
 * authority rule as a send (`runOnePass`'s own comment on why that check is repeated everywhere,
 * not assumed from the top of the pass).
 */
async function readConflictValue(outbox: Outbox, record: OutboxRecord): Promise<ConflictRead> {
  if (record.actionId === undefined || !stillMine(outbox)) return { kind: 'inconclusive' };
  try {
    const action = await sendingClient(outbox).get<ActionSummary>(apiUrl(changePath(record.actionId)), {
      timeoutMs: READ_TIMEOUT_MS,
    });
    return { kind: 'learned', theirs: extractServerValue(record, action as unknown as Record<string, unknown>) };
  } catch (error) {
    if (error instanceof HttpFailure && error.status === 404) return { kind: 'gone' };
    return { kind: 'inconclusive' };
  }
}

/**
 * The server's current value for whatever the item's own `base`/`baseNames` named — read generically
 * off the fetched action, by key, never by any domain knowledge of what a field means (constitution
 * II: nothing here computes or interprets availability, ordering, recurrence or ANY field's own
 * meaning, only carries a value keyed the same way it was asked about). A tags conflict reads `tags`
 * off the action and reports it as `{ names: [...] }` — the same key `SetActionTagsRequest`/
 * `baseNames` already use — so `theirs` is shaped identically whichever route produced it.
 */
function extractServerValue(record: OutboxRecord, action: Readonly<Record<string, unknown>>): Readonly<Record<string, unknown>> {
  if (record.baseNames !== undefined) {
    const tags = action['tags'];
    return { names: Array.isArray(tags) ? tags : [] };
  }
  const base = record.base ?? {};
  const theirs: Record<string, unknown> = {};
  for (const key of Object.keys(base)) {
    theirs[key] = key in action ? action[key] : null;
  }
  return theirs;
}

/**
 * `attemptSend`'s own internal outcome — the same three shapes `SubmitOutcome` carries, minus the
 * row's `seq` (the caller already has it). A `waiting` outcome, however many times in a row it
 * happens for the same row, never becomes a refusal on its own: FR-054/FR-057 hold the row (and
 * everything behind it) exactly as it is, and the row is sent again on the next beat. A server
 * outage or a flaky deploy is, from the practitioner's side, the same as no connection at all —
 * both wait for the server to come back rather than asking the practitioner to intervene.
 */
type AttemptOutcome =
  | { readonly kind: 'answered'; readonly result: unknown }
  /**
   * `strikeCandidate` (issue 208): whether THIS particular `waiting` outcome is the kind the
   * bad-row rule counts at all — a `5xx`, or a `2xx` that is not a well-formed answer. `status` is
   * the raw status this attempt actually saw (`200` standing in for any successful-but-malformed
   * response, since `http.ts` never hands a success's exact status up); both exist only so
   * `runOnePass` can drive `applyStrikeCandidate` and build the bad-row refusal's own reason without
   * `attemptSend` reaching into the strike bookkeeping itself.
   */
  | { readonly kind: 'waiting'; readonly strikeCandidate: boolean; readonly status: number }
  | { readonly kind: 'refused'; readonly problem: ApiError };

async function attemptSend(outbox: Outbox, record: OutboxRecord): Promise<AttemptOutcome> {
  try {
    const result = await dispatch(sendingClient(outbox), record.request.method, apiUrl(record.request.path), record.request.body, {
      headers: {
        [IDEMPOTENCY_KEY_HEADER]: record.key,
        [WIRE_VERSION_HEADER]: String(record.wireVersion),
        [MADE_AT_HEADER]: record.madeAt,
      },
      timeoutMs: SEND_TIMEOUT_MS,
    });
    // issue 208: this send's own outcome, reported to `core/reach/` exactly like any other request
    // — `{ ownSend: true }` keeps it OUT of the bad-row rule's evidence counter (a row's own traffic
    // must never count as proof of itself), while still moving the reachable/unreachable/failing
    // state, which is as real a signal from an outbox send as from anything else.
    noteOutcome(200, { ownSend: true });
    if (!isWellFormedAnswer(record, result)) return { kind: 'waiting', strikeCandidate: true, status: 200 };
    return { kind: 'answered', result };
  } catch (error) {
    if (error instanceof HttpFailure) {
      noteOutcome(error.status, { ownSend: true });
      if (isWaitingStatus(error.status)) {
        return { kind: 'waiting', strikeCandidate: error.status >= 500, status: error.status };
      }
    }
    return { kind: 'refused', problem: toApiError(error) };
  }
}

// --- the bad-row rule (issue 208, research R-005) -------------------------------------------------

/** Three, not a magic number re-typed at every call site — FR-057's own "however many times" stops
 *  being unconditional exactly here. */
const BAD_ROW_STRIKE_LIMIT = 3;

/**
 * `contracts/what-the-practitioner-sees.md`, "A row refused by repeated failure" — the reason this
 * build writes for ITSELF, since a `5xx` or a malformed `2xx` carries nothing a practitioner should
 * read verbatim (unlike a considered `4xx`'s own title/detail, which are the server's own words).
 * `status` is carried through for whoever inspects the row later; it plays no part in the wording.
 */
function badRowProblem(status: number): ApiError {
  return {
    status,
    code: 'outbox.repeated_failure',
    title: 'This could not be sent, several times, though CompleteMe’s server has otherwise been answering.',
    detail: null,
  };
}

type StrikeVerdict =
  | { readonly kind: 'still-waiting' }
  /** The row moved on (an edit or a discard) before this strike could be written — the key guard
   *  failed, exactly like an answer or a "considered" refusal arriving after the row changed. */
  | { readonly kind: 'moved-on' }
  | { readonly kind: 'refused'; readonly problem: ApiError };

/**
 * issue 208's bad-row rule: a `5xx`, or a `2xx` that is not a well-formed answer, counts against a
 * row only when some OTHER request to the API has succeeded since the row's own last strike — or
 * since it was queued, with none yet. Three counted strikes and the row becomes `refused`; a
 * failure with no fresh evidence counts for nothing, however many times it repeats (FR-054/FR-057
 * hold for exactly that case, and the describe block above this one is what proves it still does).
 *
 * `row.evidenceMark` is compared against `evidenceCount()`, never the reverse — a stored mark
 * greater than the LIVE counter is what a reload looks like (the counter restarts at zero; a mark
 * written before it does not), and is re-based down rather than read as fresh evidence, so the very
 * next real success is what counts, not however many happen to be needed to catch up to a number
 * from a session that no longer exists.
 */
async function applyStrikeCandidate(db: DeviceDatabase, row: OutboxRecord, status: number): Promise<StrikeVerdict> {
  const now = evidenceCount();
  const priorMark = row.evidenceMark ?? 0;
  const mark = Math.min(priorMark, now);
  const hasEvidence = now > mark;

  if (!hasEvidence) {
    if (mark !== priorMark) {
      // A stale mark from before a reload — re-based, but this occasion itself is not a strike.
      const wrote = await db.updateStateIfKey(row.seq, row.key, { evidenceMark: mark });
      if (!wrote) return { kind: 'moved-on' };
    }
    return { kind: 'still-waiting' };
  }

  const strikes = (row.strikes ?? 0) + 1;
  if (strikes < BAD_ROW_STRIKE_LIMIT) {
    const wrote = await db.updateStateIfKey(row.seq, row.key, { strikes, evidenceMark: now });
    return wrote ? { kind: 'still-waiting' } : { kind: 'moved-on' };
  }

  const problem = badRowProblem(status);
  const wrote = await db.updateStateIfKey(row.seq, row.key, {
    strikes, evidenceMark: now, state: 'refused', problem,
  });
  return wrote ? { kind: 'refused', problem } : { kind: 'moved-on' };
}

function settle(outbox: Outbox, seq: number, outcome: SubmitOutcome): void {
  const resolve = outbox.waiters.get(seq);
  if (!resolve) return;
  outbox.waiters.delete(seq);
  resolve(outcome);
}

/**
 * "one send loop that coalesces, no queue of passes." `kick()` starts a
 * pass if none is running; if one already is, it sets `kicked` so `runPasses` runs exactly one MORE
 * pass once the current one ends, however many `kick()`s arrived while it was busy — a burst of
 * captures made in quick succession costs one extra pass, not one per capture.
 *
 * 036 (issue 257, A1) — `outbox.sending` is reset INSIDE `runPasses`'s own `finally`, synchronously
 * with the do-while's last `kicked` check, never in a SEPARATE `.finally()` chained onto the promise
 * this function returns. The two used to be split: `runPasses`'s do-while decided to stop (reading
 * `kicked` as false), and only on the NEXT microtask — once that returned promise settled — did a
 * `.finally()` out here flip `sending` back to `false`. A `kick()` landing in exactly that gap read
 * `sending` as still `true` (stale), set `kicked = true` on the strength of it, and returned — but
 * the do-while loop had ALREADY left its last iteration and would never read `kicked` again, so the
 * coalesced pass this `kick()` promised was simply never run. Narrow, but not rare: per-row locking
 * (this same issue) removed one microtask hop `runPasses` used to spend inside `outboxLock.withLock`
 * wrapping the WHOLE pass, which had been accidentally widening this exact gap enough that nothing
 * had hit it before. Doing both — the `kicked` check and the `sending` reset — inside one synchronous
 * span (no `await` between the do-while's last iteration and the `finally` that follows it) removes
 * the gap outright: nothing else can run between them for a `kick()` to land in.
 */
function kick(outbox: Outbox): Promise<void> {
  if (outbox.closed) return Promise.resolve();
  if (outbox.sending) {
    outbox.kicked = true;
    return outbox.runningPromise ?? Promise.resolve();
  }
  outbox.sending = true;
  const running = runPasses(outbox);
  outbox.runningPromise = running;
  void running.finally(() => {
    if (outbox.runningPromise === running) outbox.runningPromise = null;
  });
  return running;
}

async function runPasses(outbox: Outbox): Promise<void> {
  try {
    do {
      outbox.kicked = false;
      await runOnePass(outbox);
    } while (outbox.kicked && !outbox.closed);
  } finally {
    outbox.sending = false;
  }
}

/**
 * What one row's own turn under the lock leaves for `runOnePass`'s own bookkeeping — shared,
 * mutable, across every row this pass claims, since `resync` and the cross-window announcement are
 * each counted once for the WHOLE PASS, never once per row (see `runOnePass`'s own comment on why).
 */
interface PassState {
  sentWithoutWaiter: boolean;
  anySent: boolean;
  anyChanged: boolean;
}

/**
 * 036 (issue 257, A1) — one row's entire turn: re-read it fresh, claim it, send it, and act on the
 * answer — ALL under the ONE `completeme.outbox.send.<practitionerId>` lock acquisition this
 * function itself takes, and ONLY for this row. This used to be the inner body of a loop that
 * acquired the lock ONCE for the whole pass (`runPasses`'s own `withLock` wrapped every row a pass
 * touched) — correct for keeping two WINDOWS from ever claiming the same row at once (SC-025,
 * SC-031 still hold here: claim-while-locked, then send, is unchanged), wrong for a withdraw, an
 * edit or a discard made IN THIS SAME WINDOW to a row further back in the queue: `withdrawChange`,
 * `replaceCapture` and `discardOutboxItem` already take this identical lock for their own one
 * transaction, so queued behind a pass that held it for twenty rows, the un-tick of row #15 could
 * not run until row #15 had already been sent — found by tracing the code, not by a flaky test
 * (issue 257, finding A1).
 *
 * Releasing the lock BETWEEN rows is the fix: a withdraw/edit/discard queued for the lock while row
 * N is on the wire is free to run the instant row N's own turn ends, before the pass ever acquires
 * the lock again for row N+1 — which is also the moment this function re-reads row N+1 fresh FROM
 * THE DEVICE, under the lock, so it sees whatever that withdraw/edit/discard just did. Nothing about
 * the per-row re-read changes; only how much of the pass shares one lock HOLD.
 *
 * Returns `'continue'` to let the pass try the next row, or `'stop'` to end this pass outright —
 * exactly the two things every branch below used to do with `continue`/`return` when this was all
 * one function's own loop body.
 */
async function sendOneRow(outbox: Outbox, db: DeviceDatabase, seq: number, state: PassState): Promise<'continue' | 'stop'> {
  if (outbox.closed) return 'stop';

  /**
   * Re-checked immediately before claiming this row, not only once before the
   * loop. An earlier row's send can itself end the session (a 401 whose renewal is then
   * definitively refused) or — the isolation finding the redesign this lock belongs to exists for —
   * another practitioner can sign in, in another window, while this pass was already scheduled.
   * Either way, this outbox's own practitioner must still be the one signed in RIGHT NOW.
   */
  if (!stillMine(outbox)) return 'stop';

  /**
   * Re-read THIS row from the device immediately before sending it, UNDER THE LOCK —
   * `allRows()`'s own snapshot was taken before this row's turn, and the practitioner can withdraw,
   * discard or edit a row (in this window or another) while an earlier row is still in flight.
   */
  const current = await db.getRow(seq);
  if (outbox.closed) return 'stop';
  if (!current || isUnreadableRow(current) || current.state !== 'waiting') return 'continue'; // moved on

  // "immediately before each request", after the row has been re-read: the
  // last possible moment this outbox's authority to send it is still checked.
  if (!stillMine(outbox)) return 'stop';

  outbox.sendingSeqs.add(current.seq);
  publishSendingSeqs(outbox);
  let outcome: AttemptOutcome;
  try {
    outcome = await attemptSend(outbox, current);
  } finally {
    outbox.sendingSeqs.delete(current.seq);
    if (!outbox.closed) publishSendingSeqs(outbox);
  }
  if (outbox.closed) return 'stop';

  if (outcome.kind === 'answered') {
    // An independent guard alongside `stillMine`: delete only the row that was actually
    // SENT, identified by its key, not merely whatever now sits at this seq. A row edited in
    // place (`replaceCapture`) while this send was in flight carries a NEW key at the SAME seq;
    // without this check a stale answer for the ORIGINAL submission would delete the EDITED
    // row, silently losing it — the server holds the new text, and the device holds nothing.
    const deleted = await db.deleteIfKey(current.seq, current.key);
    if (outbox.closed) return 'stop';
    if (!deleted) {
      // The row moved on (edited) before this answer arrived: leave it exactly as the edit left
      // it — `waiting`, under its new key — for the next pass to pick up and send for real.
      return 'continue';
    }
    const hadWaiter = outbox.waiters.has(current.seq);
    // Updated in the mirror THIS window already holds — no device read, and no
    // per-row broadcast either; a caller awaiting the answer must never see it before the
    // mirror (and hence `/unsent` and the toolbar count) does, and this update is synchronous.
    removeRowFromMirror(outbox, current.seq);
    state.anySent = true;
    if (!hadWaiter) state.sentWithoutWaiter = true; // only for an answer nobody was awaiting
    settle(outbox, current.seq, { kind: 'answered', result: outcome.result });
    return 'continue';
  }

  if (outcome.kind === 'refused') {
    /**
     * 036 (issue 246, FR-033) — a considered `409 action.edit_conflict` or `404
     * action.not_found` on an EDITABLE row (one carrying `base` or `baseNames` — a field edit or
     * a tags change; `isKnownRecord`'s own distinction) is not an ordinary refusal: the practitioner
     * has to be shown both values and choose, or told the action is gone. Scoped to editable rows
     * only, and to these two codes only — `isCompleted`/`isStarred`/a `deferUntil` ignore `base`
     * entirely and never produce `action.edit_conflict` (contracts/replayable-writes.md), so a
     * tick/star/defer 404 or 409 is untouched by this branch and keeps falling through to the
     * ordinary refusal below, exactly as it did before this slice (change.spec.ts's and
     * use-unsent-for.spec.tsx's own "refused" cases send no `base` and must stay `refused`).
     *
     * Neither branch calls `settle` — the leftover-waiter sweep in `runOnePass`'s own `finally`
     * settles any caller still awaiting this row's original `submitChange`/`submitTagsChange`
     * call as `{ kind: 'waiting' }`, the same outcome a stalled connection already produces: the
     * conflict or the gone state is discovered through the row itself (`useUnsentFor`), not
     * through the promise that queued it, which is usually long since abandoned by the time a
     * queued edit is actually sent.
     */
    const editable = current.base !== undefined || current.baseNames !== undefined;

    if (editable && outcome.problem.code === 'action.edit_conflict') {
      const read = await readConflictValue(outbox, current);
      if (outbox.closed) return 'stop';
      if (read.kind === 'learned') {
        const wrote = await db.updateStateIfKey(current.seq, current.key, { state: 'conflict', theirs: read.theirs });
        if (outbox.closed) return 'stop';
        if (wrote) {
          patchRowInMirror(outbox, current.seq, { state: 'conflict', theirs: read.theirs });
          state.anyChanged = true;
        }
        return 'continue'; // FR-010: a conflict does not hold up what is behind it
      }
      if (read.kind === 'gone') {
        const wrote = await db.updateStateIfKey(current.seq, current.key, { state: 'gone' });
        if (outbox.closed) return 'stop';
        if (wrote) {
          patchRowInMirror(outbox, current.seq, { state: 'gone' });
          state.anyChanged = true;
        }
        return 'continue';
      }
      // `read.kind === 'inconclusive'` — no answer about the conflict's own detail yet (the read
      // itself could not be tried or did not complete). Leave the row exactly `waiting`, settle
      // nobody, and stop this pass: the next one repeats the write and, on a fresh 409, the read.
      return 'stop';
    }

    if (editable && outcome.problem.code === 'action.not_found') {
      const wrote = await db.updateStateIfKey(current.seq, current.key, { state: 'gone' });
      if (outbox.closed) return 'stop';
      if (wrote) {
        patchRowInMirror(outbox, current.seq, { state: 'gone' });
        state.anyChanged = true;
      }
      return 'continue';
    }

    /**
     * The outbox always keeps a refused row — never removes one on the assumption that a
     * waiter could still show it, which is wrong the moment the practitioner has already moved
     * on, since the refusal would then land on a screen the row was never typed on. It is
     * guarded by key like an answer. `settle` below carries the row's `seq`; whoever is still
     * watching decides for itself whether it can show the reason where the line was typed, and
     * calls `dismissRefused(seq)` once it has — this file never guesses on their behalf.
     */
    const wrote = await db.updateStateIfKey(current.seq, current.key, { state: 'refused', problem: outcome.problem });
    if (outbox.closed) return 'stop';
    if (!wrote) return 'continue'; // the row moved on before this refusal arrived; leave the edit alone
    patchRowInMirror(outbox, current.seq, { state: 'refused', problem: outcome.problem });
    state.anyChanged = true;
    settle(outbox, current.seq, {
      kind: 'refused', problem: outcome.problem, seq: current.seq, practitionerId: outbox.practitionerId,
    });
    return 'continue';
  }

  // `outcome.kind === 'waiting'`. A `5xx` or a malformed `2xx` (`outcome.strikeCandidate`) is
  // counted against the row ONLY when the bad-row rule's evidence has moved since this row's own
  // last strike (issue 208) — never on the strength of a `0`/`401`/`408`, which this file has
  // never treated as anything but "try again" and still does not.
  if (outcome.strikeCandidate) {
    const verdict = await applyStrikeCandidate(db, current, outcome.status);
    if (outbox.closed) return 'stop';
    if (verdict.kind === 'moved-on') return 'continue'; // an edit or a discard landed first
    if (verdict.kind === 'refused') {
      patchRowInMirror(outbox, current.seq, { state: 'refused', problem: verdict.problem });
      state.anyChanged = true;
      settle(outbox, current.seq, {
        kind: 'refused', problem: verdict.problem, seq: current.seq, practitionerId: outbox.practitionerId,
      });
      return 'continue'; // FR-010: a refusal never holds up what is behind it, this build's own included
    }
    // `verdict.kind === 'still-waiting'`: a strike was recorded (or a stale mark re-based), but
    // fewer than three — falls through exactly like any other `waiting` outcome, below.
  }

  // However many times in a row this exact row has answered `waiting` (issue 208's own strikes
  // included, while there are fewer than three counted): no refusal the server never made. This
  // row, and everything behind it that was still waiting, stays exactly as it is (FR-054/FR-057)
  // — the sweep in `runOnePass`'s own `finally` settles every caller still waiting on one of the
  // waiters THIS pass started with, rather than leaving them to hang, and the five-second beat
  // tries again.
  return 'stop';
}

/**
 * One pass: every `waiting`, sendable row, in `seq` order (FR-057, structurally — `allRows()`
 * already hands them back that way). An item with no answer HOLDS what is behind it; a refusal
 * does not (FR-010).
 *
 * A snapshot of the waiters THIS pass started with, settled as `waiting`
 * at the end if still unresolved: a capture made mid-pass registers a NEW waiter this pass never
 * saw, so it is left alone here and settled for real by the pass `kick()` schedules next, which is
 * the one that actually reads its row and sends it.
 */
async function runOnePass(outbox: Outbox): Promise<void> {
  const waitersAtStart = new Set(outbox.waiters.keys());
  /**
   * `resync` advances once for this WHOLE PASS, not once per row: several rows
   * answered without a waiter in the same pass (the beat finding a backlog, say) must still cost
   * one re-read per screen, not one per row — `e2e/home-scale.spec.ts`'s fixed five-request count
   * exists to catch exactly a doubled (or here, multiplied) read.
   *
   * `sent`/`changed` are announced to every OTHER window, and the mirror is
   * re-read from the device, AT MOST ONCE per pass — not once per row. A pass over a 200-item
   * backlog used to cost 200 device reads in this window and 200 broadcasts to every other one; a
   * window hearing `sent` also re-reads (`advanceResync`), so that was roughly a thousand requests
   * from ONE window reconnecting. Each row still updates the mirror IT ALREADY HOLDS in place
   * (`removeRowFromMirror`/`patchRowInMirror`, purely in memory, no device access), so `/unsent`
   * still updates as each row lands rather than waiting for the pass to finish; only the REAL
   * device re-read and the cross-window announcement wait for the end.
   */
  const state: PassState = { sentWithoutWaiter: false, anySent: false, anyChanged: false };
  try {
    if (outbox.closed) return;
    // FR-008: "the session is confirmed." Nothing is attempted otherwise; whatever is waiting
    // stays waiting until a later trigger finds the session signed in as this outbox's own again.
    if (!stillMine(outbox)) return;

    const db = await dbFor(outbox);
    if (outbox.closed) return;
    const rows = await db.allRows();
    if (outbox.closed) return;

    for (const row of rows) {
      if (outbox.closed) return;
      if (isUnreadableRow(row)) continue; // FR-056/review 7: never sent, never dropped, never blocks
      if (row.state !== 'waiting') continue; // a refusal does not hold up what is behind it
      // `stillMine` is checked inside `sendOneRow`, not here too — its own first line IS this row's
      // "before re-reading it" check; checking it again here would be a second, redundant call for
      // the exact same instant, which is indistinguishable from `sendOneRow`'s "immediately before
      // the request" check shifting one call later than every test that counts them expects.

      // ONE lock acquisition for THIS row alone (issue 257, A1) — released before the next row's
      // own acquisition, so a withdraw/edit/discard queued for the same lock is never held behind
      // the whole pass, only behind whichever single row is on the wire right now.
      const result = await outboxLock.withLock(
        `completeme.outbox.send.${outbox.practitionerId}`,
        () => sendOneRow(outbox, db, row.seq, state),
      );
      if (outbox.closed) return;
      if (result === 'stop') return;
    }
  } catch (error) {
    console.warn('outbox: a send pass failed and was abandoned; queued items remain `waiting`', error);
  } finally {
    /**
     * The ONE device re-read and the ONE (at most) cross-window announcement
     * for the whole pass. The per-row updates above already reflect in THIS window's own mirror;
     * this re-read reconciles it against the device's actual state (another window's own write,
     * mid-pass, that the row-by-row shortcut could not see) exactly once, however many rows the
     * pass processed. Wrapped on its own, because the mirror must never fail a capture: a failure
     * here must not skip settling the waiters below.
     */
    if (!outbox.closed && (state.anySent || state.anyChanged)) {
      try {
        await refreshMirrorFor(outbox);
      } catch (error) {
        console.warn('outbox: mirror refresh at the end of a send pass failed', error);
      }
    }
    if (!outbox.closed) {
      if (state.anySent) announce(outbox, 'sent');
      else if (state.anyChanged) announce(outbox, 'changed');
    }
    if (state.sentWithoutWaiter) advanceResync();
    for (const seq of waitersAtStart) {
      if (outbox.waiters.has(seq)) settle(outbox, seq, { kind: 'waiting' });
    }
  }
}

/**
 * The shared trigger every "try again" moment calls (R-007): a `submitCapture`, the `online`
 * event, `visibilitychange` to visible, and the five-second beat while anything is `waiting`.
 */
export function wakeOutbox(): void {
  if (current) void kick(current);
}

/**
 * The AWAITED form `core/session/sign-out.ts`'s `signOut()` needs (FR-049: "first send what can be
 * sent"). `wakeOutbox()` is fire-and-forget for its ambient callers; a sign-out needs to know the
 * attempt has finished before it asks how many items remain — so this awaits the whole coalesced
 * run (`kick()`'s own promise resolves only once every pass it triggered, including ones coalesced
 * in behind it, has finished), not just that a pass was scheduled.
 */
export async function flushOutbox(): Promise<void> {
  const outbox = current;
  if (!outbox) return;
  await kick(outbox);
}

/**
 * Discards one item without sending it — `/unsent`'s Discard control. Refuses silently (a no-op)
 * for a row currently in flight; `/unsent` does not offer Discard on such a row.
 *
 * Takes the SAME `completeme.outbox.send.<practitionerId>` lock a send pass
 * holds for its entire run, for the length of its own one transaction. A pass holds this lock for
 * every row it processes, so a discard can now never interleave with a pass in ANY window — it
 * either runs entirely before a pass starts or entirely after one ends. This is a SECOND, INDEPENDENT
 * defence alongside the key guard in `runOnePass` (`deleteIfKey`): the lock is best-effort
 * coordination (a no-op, with a console warning, where `navigator.locks` does not exist), where the
 * key guard is a structural property of the transaction itself and holds either way.
 */
export async function discardOutboxItem(seq: number, practitionerId: number | string | null): Promise<void> {
  const outbox = current;
  // 036 (issue 257, A5) — refused unless `practitionerId` names the outbox that is CURRENT right
  // now, the same rule `dismissRefused` already follows: a screen holding a stale closure from a
  // PREVIOUS practitioner's session must not be able to reach into whoever's outbox is current now.
  if (!outbox || outbox.closed || outbox.practitionerId !== practitionerId || outbox.sendingSeqs.has(seq)) return;
  await outboxLock.withLock(`completeme.outbox.send.${outbox.practitionerId}`, async () => {
    if (outbox.closed) return;
    const db = await dbFor(outbox);
    if (outbox.closed) return;
    await db.deleteItem(seq);
  });
  // 036 (issue 257, A1) — settles any waiter still open on this row, the same as `withdrawChange`
  // already does: a caller still awaiting the ORIGINAL `submitCapture` call must not hang forever now
  // that nothing will ever answer it. Harmless (a no-op) when the waiter has already settled, which
  // is the common case.
  settle(outbox, seq, { kind: 'waiting' });
  if (outbox.closed) return;
  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after a discard failed', error);
  }
}

/**
 * The outbox always keeps a refused row now; this is how a STORE that has
 * shown the refusal (same screen, same container or tag as when the line was typed) removes it,
 * once it has. Same lock as `discardOutboxItem`/`replaceCapture` — a dismiss can never
 * interleave with a send pass in any window — and the SAME key-guard SPIRIT as an answer or a
 * refusal deleting a row: `device-database.ts`'s `deleteIfRefused` reads the row fresh and deletes
 * it only if it is STILL `refused`, so a dismiss aimed at a refusal the store already displayed
 * never removes a row that has since moved on (`replaceCapture`'s own "send again", which mints a
 * new key and returns the row to `waiting`). A no-op — silently — when the row has already moved on
 * or is already gone; there is nothing left to dismiss either way.
 */
export async function dismissRefused(seq: number, practitionerId: number | string): Promise<void> {
  const outbox = current;
  // Bound to the practitioner the refusal was shown FOR (`SubmitOutcome`'s own `practitionerId`,
  // carried since the outcome was produced): a screen holding a stale closure from a PREVIOUS
  // session — the practitioner signed out, or a different one signed in, before the practitioner
  // dismissed what they were shown — must not be able to reach into whatever outbox is current now.
  if (!outbox || outbox.closed || outbox.practitionerId !== practitionerId) return;
  try {
    await outboxLock.withLock(`completeme.outbox.send.${outbox.practitionerId}`, async () => {
      if (outbox.closed) return;
      const db = await dbFor(outbox);
      if (outbox.closed) return;
      await db.deleteIfRefused(seq);
    });
  } catch (error) {
    // Every caller fires this and moves on, so there is nobody to reject to. The row stays where
    // it is, and the unsent count still shows it.
    console.warn('outbox: dismissing a refusal failed; the row is kept', error);
    return;
  }
  if (outbox.closed) return;
  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after dismissing a refusal failed', error);
  }
}

/**
 * An edit replaces the row in place; there is no "submit a new one, then
 * delete the old" any more. That shape is what let a `waiting` row be sent by the loop AND
 * resubmitted by the practitioner at once: the loop sent the original under its own key while
 * `/unsent`'s form was busy calling `submitCapture` for a "replacement," and the server — never
 * told the two were the same line — created both.
 *
 * The device write happens under the SAME `completeme.outbox.send.<practitionerId>`
 * lock a send pass holds for its entire run (see `discardOutboxItem`'s own comment for why this is
 * one of TWO independent defences, the other being the key guard on the answer side). Without it, a
 * pass and a replace could interleave AROUND the pass's own `sendingSeqs` check — which only covers
 * the exact row currently on the wire — for example another window's pass reading and sending this
 * row between this function's own two `sendingSeqs` checks.
 */
type ReplaceLockResult =
  | { readonly ok: true; readonly replaced: boolean }
  | { readonly ok: false; readonly reason: 'gone' | 'sending' };

export async function replaceCapture(
  seq: number,
  body: unknown,
  practitionerId: number | string | null,
): Promise<ReplaceOutcome> {
  const outbox = current;
  // 036 (issue 257, A5) — refused unless `practitionerId` names the outbox that is CURRENT right
  // now, the same rule `dismissRefused` already follows.
  if (!outbox || outbox.closed || outbox.practitionerId !== practitionerId) return { kind: 'refused', reason: 'gone' };
  if (outbox.sendingSeqs.has(seq)) return { kind: 'refused', reason: 'sending' };

  let result: ReplaceLockResult;
  try {
    result = await outboxLock.withLock(`completeme.outbox.send.${outbox.practitionerId}`, async (): Promise<ReplaceLockResult> => {
      if (outbox.closed) return { ok: false, reason: 'gone' };
      if (outbox.sendingSeqs.has(seq)) return { ok: false, reason: 'sending' };
      const db = await dbFor(outbox);
      if (outbox.closed) return { ok: false, reason: 'gone' };
      if (outbox.sendingSeqs.has(seq)) return { ok: false, reason: 'sending' };
      // issue 208: a fresh baseline for the bad-row rule too — "send again" restarts the "since it
      // was queued" clock at NOW, not at the row's original queue time or at zero.
      const replaced = await db.replaceCapture(seq, body, clock().toISOString(), evidenceCount());
      return { ok: true, replaced };
    });
  } catch (error) {
    // FR-013: the device itself would not write the replacement (private browsing, blocked site
    // data, a full disk). The ORIGINAL row is untouched — nothing above this point wrote anything.
    console.warn('outbox: replaceCapture failed', error);
    return { kind: 'refused', reason: 'device' };
  }
  if (!result.ok) return { kind: 'refused', reason: result.reason };
  if (!result.replaced) return { kind: 'refused', reason: 'gone' };
  if (outbox.closed) return { kind: 'ok' };

  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after an edit failed', error);
  }
  void kick(outbox);
  return { kind: 'ok' };
}

// --- the FR-013 fallback: a device that will not store anything --------------------------------

async function sendWithoutStoring<T>(body: unknown): Promise<SubmitOutcome<T>> {
  // Not a queued item and not an envelope-carrying request: this is the pre-outbox behaviour,
  // unchanged, because there is nothing durable to give the envelope's guarantees meaning for.
  const result = await appClient().post<T>(apiUrl(CAPTURE_PATH), body);
  return { kind: 'answered', result };
}

/** The same FR-013 fallback, for a change: sent directly, with no envelope, exactly as this build's
 *  editors already PATCH today when the device will not keep anything durably. */
async function sendChangeWithoutStoring<T>(actionId: number, body: unknown): Promise<SubmitOutcome<T>> {
  const result = await appClient().patch<T>(apiUrl(changePath(actionId)), body);
  return { kind: 'answered', result };
}

/** The same FR-013 fallback, for a tags change: sent directly as the `PUT` `tag-editor.tsx` already
 *  makes today when the device will not keep anything durably. */
async function sendTagsChangeWithoutStoring<T>(actionId: number, body: unknown): Promise<SubmitOutcome<T>> {
  const result = await appClient().put<T>(apiUrl(tagsPath(actionId)), body);
  return { kind: 'answered', result };
}

// --- submitCapture ---------------------------------------------------------------------------

/**
 * 036 (issue 257, A3) — the one signal every caller needs to stop holding the capture box busy:
 * fired the moment the item is DURABLY on the device, well before this function's own promise
 * settles with the eventual send outcome. "The box is free again as soon as the line is durably on
 * the device" (issue 257's own fix direction) — this is the hook that moment is reported through,
 * never invoked on the FR-013 fallback path (`sendWithoutStoring`/`sendChangeWithoutStoring`), where
 * there is nothing durable to report: that path still resolves only once the direct request answers,
 * exactly as it always has.
 */
export interface QueueOptions {
  readonly onQueued?: () => void;
}

/**
 * Writes `body` to the device as a capture, then tries to send it. See "How `submit` behaves"
 * (issue 207) for the three outcomes; where the device will not keep it, this falls back to
 * sending directly and either resolves `answered` or throws exactly as `appClient().post` always
 * has (FR-013).
 *
 * The ONLY export a feature store or component may call to reach the
 * capture route; neither the path nor the request shape is visible outside `core/outbox/` any
 * more. `only-door.spec.ts` polices the boundary this narrows to.
 */
export async function submitCapture<T = unknown>(body: unknown, options?: QueueOptions): Promise<SubmitOutcome<T>> {
  const practitionerId = account.get()?.practitionerId ?? null;
  if (practitionerId === null) return sendWithoutStoring<T>(body);

  const outbox = ensureCurrentFor(practitionerId);
  if (!outbox) return sendWithoutStoring<T>(body);

  const record: NewOutboxRecord = {
    key: randomKey(),
    wireVersion: CURRENT_WIRE_VERSION,
    kind: 'capture',
    madeAt: clock().toISOString(),
    state: 'waiting',
    request: { method: 'POST', path: CAPTURE_PATH, body },
    // issue 208: the bad-row rule's baseline — a candidate failure on this row only ever counts
    // against evidence observed AFTER this moment.
    evidenceMark: evidenceCount(),
  };

  /**
   * "this browser will not let CompleteMe keep anything" means the DEVICE, not
   * a closed handle. `dbFor(outbox)` can hand back a handle that looks healthy at the moment it is
   * checked and then be closed by ANOTHER connection (another tab's confirmed "sign out and
   * delete", or this very outbox stepping aside for a fresher open) in the narrow window before
   * `addCapture` actually runs against it — `InvalidStateError`, thrown by a device that would have
   * happily stored this a moment earlier or a moment later. The bare `catch` below used to treat
   * that identically to a genuinely full disk or blocked storage, showing FR-013's message on a
   * device that was never asked in good faith.
   */
  let seq: number;
  try {
    const db = await dbFor(outbox);
    seq = await db.addCapture(record);
  } catch {
    if (outbox.closed) {
      // This outbox stopped being current while the add was in flight (a sign-out, another
      // practitioner signing in) — nothing here is this browser's fault, and nothing is left to
      // retry against.
      return sendWithoutStoring<T>(body);
    }
    try {
      outbox.db = null; // never reuse the handle that just threw
      const freshDb = await dbFor(outbox);
      seq = await freshDb.addCapture(record);
    } catch {
      // Opening failed outright, or the SECOND add did too — the device really would not store it.
      if (outbox === current) mirror.setState({ cannotStore: true });
      return sendWithoutStoring<T>(body);
    }
  }

  // The item is durably on the device (FR-006) even if this outbox has, in the meantime, stopped
  // being the current one — nothing is lost, but there is no live outbox left to register a
  // waiter with, so this caller is simply told the truth: it is queued, waiting for whoever picks
  // it up next.
  if (outbox.closed) return { kind: 'waiting' };

  if (outbox === current) mirror.setState({ cannotStore: false });

  // SC-020's own fix: draw the mark NOW — synchronous, no device round trip — rather than waiting
  // for the full `refreshMirrorFor` scan below (`insertRowIntoMirror`'s own comment has the
  // measurement). `useUnsentFor`'s subscribers re-render off THIS `mirror.setState`, not off
  // `submitCapture` returning, so moving it earlier is the whole fix.
  insertRowIntoMirror(outbox, record, seq);

  // 036 (issue 257, A3) — "durably on the device" is true RIGHT HERE: `addCapture` has already
  // resolved and the mirror already carries the row. Fired before anything below that could itself
  // wait on the network (the send loop's own `dispatch`) or on IndexedDB (the mirror's full
  // reconciliation read) — a caller freeing its busy flag from inside this callback is never held by
  // either.
  options?.onQueued?.();

  /**
   * "send sooner." The item is on the device already (FR-006's whole requirement);
   * refreshing the mirror and announcing to other windows are NOT part of that requirement.
   * `waiters.set` then `kick` happen FIRST so the send loop's own microtask chain toward `dispatch`
   * is already progressing while the mirror refresh and announcement run alongside it.
   */
  const outcomePromise = new Promise<SubmitOutcome>((resolve) => {
    outbox.waiters.set(seq, resolve);
    void kick(outbox);
  });

  // "the mirror can never fail a capture." A refresh that throws here
  // (a transient IndexedDB failure reading the very row that was just written) is logged and
  // swallowed rather than rejecting this promise: the row is on the device and already being sent;
  // failing the caller would make the store report a failed capture and the practitioner would
  // retype it under a brand-new key while the first is still on its way. `insertRowIntoMirror`
  // above already drew the mark; this is the reconciliation pass (cross-window announcement, and
  // correctness against anything the fast path could not see), not a gate on it any more.
  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after a capture failed; the capture is still queued', error);
  }

  const outcome = await outcomePromise;
  return outcome as SubmitOutcome<T>;
}

// --- submitChange (036 T089) --------------------------------------------------------------------

/**
 * What `submitChange` needs to queue a change: the action it changes, the PATCH body that performs
 * it, and `shows` — what the row should draw while it waits (data-model.md's own field; FR-028,
 * FR-030). `shows` is never inspected here, only stored and handed back by `useUnsentFor` — this
 * file has no opinion about what a screen draws from it (the rule that must not be broken: a queued
 * change draws only itself, and nothing here computes availability, ordering, recurrence or a
 * successor).
 */
export interface ChangeInput {
  readonly actionId: number;
  readonly body: Readonly<Record<string, unknown>>;
  readonly shows: Readonly<Record<string, unknown>>;
  /**
   * 036 (issue 246, FR-030) — the field's own value when the practitioner made this edit. Merged
   * into the wire body as `base` (contracts/replayable-writes.md's own PATCH shape:
   * `{ description: "...", base: { description: "..." } }`) and mirrored on the queued record's own
   * top level, so a conflict can be resolved (`readConflictValue`/`extractServerValue`) without
   * reaching back into `request.body`. Omitted by a caller whose field never conflicts
   * (`isCompleted`, `isStarred`, a `deferUntil` — FR-031) — `submitOrWithdraw`'s own three callers
   * queue exactly that way today, unchanged.
   */
  readonly base?: Readonly<Record<string, unknown>>;
}

/**
 * The `change` counterpart to `submitCapture` (see that function's own comment for the shape this
 * mirrors: write to the device first, THEN try to send). The only door for a `PATCH
 * /api/v1/actions/{actionId}` made through the outbox — `only-door.spec.ts`'s T094 extension polices
 * the boundary the same way it already does for a capture, by forbidding the identifiers this file
 * builds a change's route from (`CHANGE_PATH_PREFIX`) to anyone outside `core/outbox/`.
 */
export async function submitChange<T = unknown>(input: ChangeInput, options?: QueueOptions): Promise<SubmitOutcome<T>> {
  const practitionerId = account.get()?.practitionerId ?? null;
  const body: Readonly<Record<string, unknown>> =
    input.base !== undefined ? { ...input.body, base: input.base } : input.body;
  if (practitionerId === null) return sendChangeWithoutStoring<T>(input.actionId, body);

  const outbox = ensureCurrentFor(practitionerId);
  if (!outbox) return sendChangeWithoutStoring<T>(input.actionId, body);

  const withBase: Partial<Pick<OutboxRecord, 'base'>> = input.base !== undefined ? { base: input.base } : {};
  const record: NewOutboxRecord = {
    key: randomKey(),
    wireVersion: CURRENT_WIRE_VERSION,
    kind: 'change',
    madeAt: clock().toISOString(),
    state: 'waiting',
    request: { method: CHANGE_METHOD, path: changePath(input.actionId), body },
    actionId: input.actionId,
    shows: input.shows,
    evidenceMark: evidenceCount(),
    ...withBase,
  };

  // See `submitCapture`'s own comment on this exact shape: a handle that looks healthy and is then
  // closed out from under the add (another tab's confirmed sign-out) gets exactly one fresh retry
  // before this build concludes the device itself will not store anything.
  let seq: number;
  try {
    const db = await dbFor(outbox);
    seq = await db.addCapture(record);
  } catch {
    if (outbox.closed) return sendChangeWithoutStoring<T>(input.actionId, body);
    try {
      outbox.db = null;
      const freshDb = await dbFor(outbox);
      seq = await freshDb.addCapture(record);
    } catch {
      if (outbox === current) mirror.setState({ cannotStore: true });
      return sendChangeWithoutStoring<T>(input.actionId, body);
    }
  }

  if (outbox.closed) return { kind: 'waiting' };
  if (outbox === current) mirror.setState({ cannotStore: false });

  // SC-020's own fix — see `submitCapture`'s identical comment and `insertRowIntoMirror`'s own for
  // the measurement: draw the mark now, synchronously, rather than waiting for the full
  // `refreshMirrorFor` scan below.
  insertRowIntoMirror(outbox, record, seq);

  // 036 (issue 257, A3) — see `submitCapture`'s identical comment: durable NOW, well before the send
  // outcome. The editor's own `save()` is this callback's reason for existing — "the editor closes
  // once every field is queued", not once every field is answered.
  options?.onQueued?.();

  const outcomePromise = new Promise<SubmitOutcome>((resolve) => {
    outbox.waiters.set(seq, resolve);
    void kick(outbox);
  });

  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after a change failed; the change is still queued', error);
  }

  const outcome = await outcomePromise;
  return outcome as SubmitOutcome<T>;
}

/**
 * 036 (issue 246, FR-030) — what `submitTagsChange` needs to queue a tags change: the action, the
 * full tag set the practitioner assembled, the set they were looking at when they started (FR-030),
 * and `shows` — what the row draws while it waits, the same rule `ChangeInput.shows` follows.
 */
export interface TagsChangeInput {
  readonly actionId: number;
  readonly names: readonly string[];
  readonly baseNames: readonly string[];
  readonly shows: Readonly<Record<string, unknown>>;
}

/**
 * The tags counterpart to `submitChange` — the only door for a `PUT
 * /api/v1/actions/{actionId}/tags` made through the outbox. `only-door.spec.ts`'s T115 extension
 * polices the boundary the same way it already does for a capture and a field-edit change, by
 * forbidding the identifier this file builds a tags change's route from (`TAGS_PATH_SUFFIX`) to
 * anyone outside `core/outbox/`.
 */
export async function submitTagsChange<T = unknown>(input: TagsChangeInput): Promise<SubmitOutcome<T>> {
  const body = { names: [...input.names], baseNames: [...input.baseNames] };
  const practitionerId = account.get()?.practitionerId ?? null;
  if (practitionerId === null) return sendTagsChangeWithoutStoring<T>(input.actionId, body);

  const outbox = ensureCurrentFor(practitionerId);
  if (!outbox) return sendTagsChangeWithoutStoring<T>(input.actionId, body);

  const record: NewOutboxRecord = {
    key: randomKey(),
    wireVersion: CURRENT_WIRE_VERSION,
    kind: 'change',
    madeAt: clock().toISOString(),
    state: 'waiting',
    request: { method: TAGS_METHOD, path: tagsPath(input.actionId), body },
    actionId: input.actionId,
    shows: input.shows,
    baseNames: [...input.baseNames],
    evidenceMark: evidenceCount(),
  };

  let seq: number;
  try {
    const db = await dbFor(outbox);
    seq = await db.addCapture(record);
  } catch {
    if (outbox.closed) return sendTagsChangeWithoutStoring<T>(input.actionId, body);
    try {
      outbox.db = null;
      const freshDb = await dbFor(outbox);
      seq = await freshDb.addCapture(record);
    } catch {
      if (outbox === current) mirror.setState({ cannotStore: true });
      return sendTagsChangeWithoutStoring<T>(input.actionId, body);
    }
  }

  if (outbox.closed) return { kind: 'waiting' };
  if (outbox === current) mirror.setState({ cannotStore: false });

  insertRowIntoMirror(outbox, record, seq);

  const outcomePromise = new Promise<SubmitOutcome>((resolve) => {
    outbox.waiters.set(seq, resolve);
    void kick(outbox);
  });

  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after a tags change failed; the change is still queued', error);
  }

  const outcome = await outcomePromise;
  return outcome as SubmitOutcome<T>;
}

/** What `withdrawChange` resolves to (FR-027, SC-038). `'sending'` — the row is on the wire right
 *  now; a moment later this would succeed. `'gone'` — the row no longer exists (already sent,
 *  already withdrawn, or never existed) — withdrawing twice is not an error, the second simply has
 *  nothing left to do. */
export type WithdrawOutcome =
  | { readonly kind: 'ok' }
  | { readonly kind: 'refused'; readonly reason: 'sending' | 'gone' };

/**
 * FR-027: "A change not yet sent MUST be withdrawable." Removes a `waiting` row and produces no
 * request for it — SC-038 counts this at the REQUESTS the fake client receives, never at rows, and
 * this function is why that holds: the row is gone from the device before any pass can ever read it,
 * under the same send lock a pass holds for its own entire run (`discardOutboxItem`'s own comment
 * has the full argument for why that lock is one of two independent defences, the other being the
 * key guard `deleteIfKey`/`updateStateIfKey` already give an answer or a refusal arriving late).
 *
 * A row already `refused` is not "not yet sent" in FR-027's sense — it WAS sent, and the server
 * answered no; that is `dismissRefused`'s row to remove, once a caller has shown the reason. Withdraw
 * only ever removes a `waiting` row.
 */
export async function withdrawChange(seq: number, practitionerId: number | string | null): Promise<WithdrawOutcome> {
  const outbox = current;
  // 036 (issue 257, A5) — refused unless `practitionerId` names the outbox that is CURRENT right
  // now, the same rule `dismissRefused` already follows.
  if (!outbox || outbox.closed || outbox.practitionerId !== practitionerId) return { kind: 'refused', reason: 'gone' };
  if (outbox.sendingSeqs.has(seq)) return { kind: 'refused', reason: 'sending' };

  let deleted = false;
  await outboxLock.withLock(`completeme.outbox.send.${outbox.practitionerId}`, async () => {
    if (outbox.closed) return;
    if (outbox.sendingSeqs.has(seq)) return;
    const db = await dbFor(outbox);
    if (outbox.closed) return;
    const row = await db.getRow(seq);
    if (!row || isUnreadableRow(row) || row.state !== 'waiting') return;
    await db.deleteItem(seq);
    deleted = true;
  });

  if (!deleted) {
    return { kind: 'refused', reason: outbox.sendingSeqs.has(seq) ? 'sending' : 'gone' };
  }

  // A withdraw settles any waiter still open on this row — a caller awaiting the ORIGINAL
  // `submitChange` call must not hang forever now that nothing will ever answer it. Harmless (a
  // no-op) when the waiter has already settled, which is the common case (the change has almost
  // always already resolved `waiting` by the time a practitioner acts to withdraw it).
  settle(outbox, seq, { kind: 'waiting' });

  if (outbox.closed) return { kind: 'ok' };
  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after a withdraw failed', error);
  }
  return { kind: 'ok' };
}

// --- answering a conflict (issue 246, FR-033) — data-model.md's own "keep mine" / "keep theirs" --

/** `keepMine`/`keepTheirs`'s own answer. `'refused', reason: 'gone'` — the row has moved on before
 *  the answer arrived (already withdrawn some other way, or never existed): there is nothing left to
 *  keep either mine or theirs. Not `'sending'`: a `conflict` row is never on the wire (`runOnePass`
 *  only ever writes `conflict` on a row it has already finished sending), so there is no in-flight
 *  case to distinguish here the way `withdrawChange`/`discardOutboxItem` must.
 *  036 (issue 257, A4) — `'device'`: the device itself would not write the change (a full disk,
 *  blocked site data). The conflicted row is left exactly as it was — neither function writes
 *  anything until it knows the whole change can be made, so there is nothing to roll back. */
export type ConflictAnswerOutcome =
  | { readonly kind: 'ok' }
  | { readonly kind: 'refused'; readonly reason: 'gone' | 'device' };

/**
 * "Keep theirs": discards the conflicted item outright (data-model.md: `conflict --> [*]`). Nothing
 * is sent — the server's value already IS the server's value; there is nothing left to ask it for.
 * Same send lock as `withdrawChange`/`discardOutboxItem`, for the same reason: a discard must never
 * interleave with a pass in any window.
 */
export async function keepTheirs(seq: number, practitionerId: number | string | null): Promise<ConflictAnswerOutcome> {
  const outbox = current;
  // 036 (issue 257, A5) — refused unless `practitionerId` names the outbox that is CURRENT right
  // now, the same rule `dismissRefused` already follows.
  if (!outbox || outbox.closed || outbox.practitionerId !== practitionerId) return { kind: 'refused', reason: 'gone' };

  let deleted = false;
  try {
    await outboxLock.withLock(`completeme.outbox.send.${outbox.practitionerId}`, async () => {
      if (outbox.closed) return;
      const db = await dbFor(outbox);
      if (outbox.closed) return;
      const row = await db.getRow(seq);
      if (!row || isUnreadableRow(row) || row.state !== 'conflict') return;
      await db.deleteItem(seq);
      deleted = true;
    });
  } catch (error) {
    // 036 (issue 257, A4) — hardened alongside `keepMine`: the device itself would not take the
    // delete. The conflicted row is left exactly as it was (nothing else in this transaction wrote
    // anything), shown by the mirror's own next refresh, which still finds it in `conflict`.
    console.warn('outbox: keepTheirs failed; the conflicted row is kept', error);
    return { kind: 'refused', reason: 'device' };
  }

  if (!deleted) return { kind: 'refused', reason: 'gone' };
  if (outbox.closed) return { kind: 'ok' };
  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after keeping the server\'s value failed', error);
  }
  return { kind: 'ok' };
}

/**
 * "Keep mine": data-model.md's own `conflict --> waiting: "keep mine" — a new item, base is the
 * server's value`. The conflicted row is replaced — never resent under its OWN key, since that key's
 * `base` has already been proven stale by the very 409 that found this conflict — by a FRESH item,
 * new key, new `madeAt`, carrying the SAME target value the practitioner asked for (`request.body`,
 * minus its old `base`/`baseNames`) but a `base`/`baseNames` of the server's value this item's own
 * `theirs` learned. Queued and kicked exactly like any other fresh item; "neither sends anything by
 * itself beyond that" (issue 246, criterion 5) is what makes this an ordinary queue-and-send rather
 * than a special-cased one.
 *
 * Same send lock as `keepTheirs`, for the same reason. Refuses (`reason: 'gone'`) for a row that has
 * moved on — not currently in `conflict`, or gone outright — there is nothing left to keep.
 *
 * 036 (issue 257, A4) — the delete and the add are now ONE device transaction
 * (`db.replaceWithFreshRecord`), not two. The previous shape — `db.deleteItem(seq)` then
 * `db.addCapture(record)`, each its own transaction — could lose the practitioner's value outright:
 * if the add failed (a full disk, blocked site data) AFTER the delete had already committed, the
 * conflicted row was simply gone, with nothing to replace it — breaking FR-056's own "never dropped"
 * promise for exactly the row this screen exists to protect. One transaction makes the failure leave
 * the ORIGINAL conflicted row in place, and `unsent-page.tsx` now shows that failure rather than
 * discarding it silently (issue 257's own second half of A4: "`unsent-page.tsx` has no catch").
 */
export async function keepMine(seq: number, practitionerId: number | string | null): Promise<ConflictAnswerOutcome> {
  const outbox = current;
  // 036 (issue 257, A5) — refused unless `practitionerId` names the outbox that is CURRENT right
  // now, the same rule `dismissRefused` already follows.
  if (!outbox || outbox.closed || outbox.practitionerId !== practitionerId) return { kind: 'refused', reason: 'gone' };

  let queued = false;
  try {
    await outboxLock.withLock(`completeme.outbox.send.${outbox.practitionerId}`, async () => {
      if (outbox.closed) return;
      const db = await dbFor(outbox);
      if (outbox.closed) return;
      const row = await db.getRow(seq);
      if (!row || isUnreadableRow(row) || row.state !== 'conflict' || !row.theirs) return;

      const oldBody = row.request.body as Readonly<Record<string, unknown>>;
      const isTags = row.baseNames !== undefined;

      const record: NewOutboxRecord = isTags
        ? {
            key: randomKey(),
            wireVersion: CURRENT_WIRE_VERSION,
            kind: 'change',
            madeAt: clock().toISOString(),
            state: 'waiting',
            request: {
              method: row.request.method,
              path: row.request.path,
              body: { ...oldBody, baseNames: Array.isArray(row.theirs['names']) ? row.theirs['names'] : [] },
            },
            actionId: row.actionId,
            shows: row.shows,
            baseNames: Array.isArray(row.theirs['names']) ? (row.theirs['names'] as string[]) : [],
            evidenceMark: evidenceCount(),
          }
        : {
            key: randomKey(),
            wireVersion: CURRENT_WIRE_VERSION,
            kind: 'change',
            madeAt: clock().toISOString(),
            state: 'waiting',
            request: { method: row.request.method, path: row.request.path, body: { ...oldBody, base: row.theirs } },
            actionId: row.actionId,
            shows: row.shows,
            base: row.theirs,
            evidenceMark: evidenceCount(),
          };

      // ONE transaction: the conflicted row is replaced, never resent under its own key, and never
      // dropped on the strength of a delete that committed with no add to follow it.
      const newSeq = await db.replaceWithFreshRecord(seq, record);
      queued = newSeq !== null;
    });
  } catch (error) {
    // FR-013/A4: the device itself would not write the replacement. The transaction aborted before
    // either half committed, so the ORIGINAL conflicted row survives untouched.
    console.warn('outbox: keepMine failed; the conflicted row is kept', error);
    return { kind: 'refused', reason: 'device' };
  }

  if (!queued) return { kind: 'refused', reason: 'gone' };
  if (outbox.closed) return { kind: 'ok' };

  try {
    await refreshMirrorFor(outbox);
    announce(outbox, 'changed');
  } catch (error) {
    console.warn('outbox: mirror refresh after keeping my own value failed', error);
  }
  void kick(outbox);
  return { kind: 'ok' };
}

// --- the holder: driven by the session, and nothing else ----------------------------------------

/**
 * "one holder, driven by the session." Signed in as P with no outbox, or
 * with an outbox for someone else, closes the old one (if any) and creates P's. Session ended (or
 * never started) closes it and holds nothing. Called both reactively, by `watchSession` below on
 * every practitioner change, and defensively by every public function above before touching
 * `current` — the two should always agree (`onPractitionerChange` fires synchronously inside the
 * `setState` that changes it), but a function that trusted a stale `current` without checking is
 * exactly the class of defect this file exists to close off.
 */
function ensureCurrentFor(practitionerId: number | string | null): Outbox | null {
  if (current && current.practitionerId === practitionerId && !current.closed) return current;
  if (current) closeOutbox(current);
  if (practitionerId === null) {
    current = null;
    mirror.setState({ ...EMPTY_MIRROR });
    return null;
  }
  current = createOutbox(practitionerId);
  mirror.setState({ ...EMPTY_MIRROR, practitionerId });
  // "every window listens from the moment its outbox object exists, not
  // from its first write." Opened here rather than left to `announce()`'s own lazy `ensureChannel`
  // call, so a window that never captures anything itself still hears `sent`/`changed`/`deleted`
  // from a window that does.
  ensureChannel(current);
  return current;
}

let unwatchSession: (() => void) | null = null;

function watchSession(): void {
  if (unwatchSession) return;
  unwatchSession = onPractitionerChange((practitionerId) => {
    ensureCurrentFor(practitionerId);
  });
}

watchSession();

/**
 * Loads the mirror for whoever is signed in now, or clears it when no one is. `/unsent` and the
 * toolbar count call this on mount so they show the right practitioner's items even if
 * `submitCapture` has not run yet in this tab.
 */
export async function refreshOutboxMirror(): Promise<void> {
  const practitionerId = account.get()?.practitionerId ?? null;
  const outbox = ensureCurrentFor(practitionerId);
  if (!outbox) return;
  await refreshMirrorFor(outbox);
}

// --- lifecycle ---------------------------------------------------------------------------------

let watching = false;
let unsubscribeBeat: (() => void) | null = null;
let unsubscribeSignedIn: (() => void) | null = null;

/**
 * Wires the ambient triggers (R-007): `core/reach/`'s ONE shared beat (the `online` event,
 * `visibilitychange` to visible, and a five-second poll while the document is visible AND this
 * outbox has something waiting), plus the session becoming `signed-in`, so a newly signed-in
 * session with waiting items begins sending AT ONCE rather than within the beat's five seconds.
 * Idempotent, because `StrictMode` double-invokes a mount effect and this must be safe to call
 * twice — the guard is the module-level `watching` flag, not a React ref, since this is called once
 * from `main.tsx` and not from a component at all.
 *
 * issue 208 moved the timer itself (and its own `online`/`visibilitychange` listeners) into
 * `core/reach/reach.store.ts`'s `subscribeToBeat` — this file used to own a `setInterval` and both
 * listeners directly, duplicating exactly what reachability tracking also needs one of. `hasWork` is
 * consulted only for the recurring poll; `online` and becoming visible still fire regardless, same
 * as when this file registered them itself.
 */
export function startOutboxWatch(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;

  unsubscribeBeat = subscribeToBeat({
    hasWork: () => mirror.getState().rows.some((r) => !isUnreadableRow(r) && r.state === 'waiting'),
    onBeat: wakeOutbox,
  });

  unsubscribeSignedIn = onSignedIn(wakeOutbox);
}

/**
 * Deletes one practitioner's whole device database — signing out with unsent items discarded
 * (FR-049). `practitionerId` is always the CURRENT outbox's own (its one caller,
 * `core/session/sign-out.ts`'s `confirmSignOutDiscardingUnsent`, reads it from the session that is
 * about to end) — asserted rather than trusted, since deleting a database that is not this
 * outbox's own would be exactly the isolation defect this file exists to close off.
 */
/** How long a confirmed "sign out and delete" waits for the delete itself. */
const DELETE_TIMEOUT_MS = 5_000;

/**
 * Closes the WHOLE outbox object first, not merely its device handle, so nothing still holding a
 * reference to it can reopen a fresh connection to the database this is deleting while the delete
 * is still in flight. `announce`d before closing, since `closeOutbox` also closes the channel other
 * windows need to hear `deleted` on.
 *
 * Bounded at five seconds: a delete blocked by a connection this file cannot itself reach must not
 * hang sign-out forever. Returns whether the delete is confirmed to have completed — `sign-out.ts`
 * says so to the practitioner when it has not.
 *
 * The delete's own promise is raced against the timeout rather than awaited directly, and its
 * rejection is handled with `.then(onFulfilled, onRejected)` rather than left to a `try`/`catch`
 * around the `race`: a rejection arriving AFTER the timeout has already decided the race would
 * otherwise have nothing left awaiting it. The outer `catch` below is for the different case of
 * `deleteDeviceDatabase` throwing synchronously, before it ever returns a promise the race could
 * hold.
 */
export async function discardOutboxFor(practitionerId: number | string): Promise<boolean> {
  const outbox = current;
  if (!outbox || outbox.practitionerId !== practitionerId) return true; // nothing of this practitioner's here
  announce(outbox, 'deleted');
  closeOutbox(outbox);
  if (current === outbox) current = null;
  mirror.setState({ ...EMPTY_MIRROR, practitionerId });

  let completed = false;
  try {
    await Promise.race([
      deleteDeviceDatabase(practitionerId).then(
        () => {
          completed = true;
        },
        (error: unknown) => {
          console.warn('outbox: discarding the device database failed', error);
        },
      ),
      new Promise<void>((resolve) => setTimeout(resolve, DELETE_TIMEOUT_MS)),
    ]);
  } catch (error) {
    console.warn('outbox: discarding the device database failed outright', error);
  }
  return completed;
}

/** Test seam, like the other stores'. Production code never calls it. */
export function resetOutbox(): void {
  if (current) closeOutbox(current);
  current = null;
  mirror.setState({ ...EMPTY_MIRROR });
  watching = false;
  unsubscribeBeat?.();
  unsubscribeBeat = null;
  unsubscribeSignedIn?.();
  unsubscribeSignedIn = null;
}
